import type { RiskAssessmentResultConfig } from '@risksmart-app/domain/src/types';
import type { InsertRiskAssessmentResultConfigMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

/**
 * Maps tRPC mutation response to match the GraphQL mutation structure
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
  Version: number;
  IsLatest: boolean;
}): InsertRiskAssessmentResultConfigMutation => ({
  insertRiskAssessmentResultConfigApi: {
    __typename: 'InsertRiskAssessmentResultConfigApiOutput',
    Id: trpcData.Id,
    Version: trpcData.Version,
    IsLatest: trpcData.IsLatest,
  },
});

export const useInsertRiskAssessmentResultConfigTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.riskAssessmentResultConfig.insert.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.riskAssessmentResultConfig.getLatest.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.riskAssessmentResultConfig.getByIds.queryKey(),
        });
      },
    }),
    throwOnError: true,
  });

  return {
    mutate: async (variables: {
      variables: { Config: RiskAssessmentResultConfig; Type: string };
    }): Promise<InsertRiskAssessmentResultConfigMutation> => {
      const result = await mutation.mutateAsync({
        Config: variables.variables.Config,
        Type: variables.variables.Type as
          | 'risk_assessment'
          | 'compliance_monitoring'
          | 'internal_audit',
      });

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
